import { create } from 'zustand';
import { ListUnits, CreateUnit, GetUnit, UpdateUnit, DeleteUnit, CreateUnitI, UpdateUnitI } from '../api/api';
import toast from 'react-hot-toast';

export interface Unit {
  _id: string;
  name: string;
  unitNumber: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetUnitsResponse {
  data: {
    units: Unit[];
    pagination?: PaginationInfo;
  };
}

interface UnitResponse {
  data: {
    unit: Unit;
  };
  status: boolean;
  message: string;
}

interface DeleteUnitResponse {
  status: boolean;
  message: string;
}

interface UnitStore {
  units: Unit[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getUnits: (search?: string, page?: number, limit?: number) => Promise<void>;
  createUnit: (unit: CreateUnitI) => Promise<void>;
  getUnit: (id: string) => Promise<Unit | null>;
  updateUnit: (id: string, unit: UpdateUnitI) => Promise<void>;
  deleteUnit: (id: string) => Promise<void>;
}

export const useUnitStore = create<UnitStore>((set, get) => ({
  units: [] as Unit[],
  loading: false,
  error: null,
  pagination: null,

  getUnits: async (search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListUnits<GetUnitsResponse>(search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      units: data.data.units || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createUnit: async (unit: CreateUnitI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateUnit<UnitResponse>(unit);

    if (error || !data.status) {
      const errorMessage = error || (data as any)?.message || 'Failed to create unit';
      set({ loading: false, error: errorMessage });
      // Error toast is already shown in the API function
      return;
    }

    const units = get().units;
    set({ units: [...units, data.data.unit], loading: false });
    toast.success('Unit created successfully');
  },

  getUnit: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetUnit<UnitResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get unit' });
      return null;
    }

    set({ loading: false });
    return data.data.unit;
  },

  updateUnit: async (id: string, unit: UpdateUnitI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateUnit<UnitResponse>(id, unit);

    if (error || !data.status) {
      const errorMessage = error || (data as any)?.message || 'Failed to update unit';
      set({ loading: false, error: errorMessage });
      // Error toast is already shown in the API function
      return;
    }

    const units = get().units;
    const updatedUnits = units.map((u) => (u._id === id ? { ...u, ...data.data.unit } : u));
    set({ units: updatedUnits, loading: false });
    toast.success('Unit updated successfully');
  },

  deleteUnit: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteUnit<DeleteUnitResponse>(id);

    if (error || !data.status) {
      const errorMessage = error || (data as any)?.message || 'Failed to delete unit';
      set({ loading: false, error: errorMessage });
      // Error toast is already shown in the API function
      return;
    }

    const units = get().units.filter((unit) => unit._id !== id);
    set({ units, loading: false });
    toast.success('Unit deleted successfully');
  },
}));
